import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRealtime } from '../contexts/RealtimeContext';
import { useLocationContext } from '../contexts/LocationContext';
import LocationSwitcher from './LocationSwitcher';
import {
  Gauge, Users, Cube, Factory, ClipboardText,
  Package, Truck, CalendarBlank, CurrencyInr, Warning, SignOut, List, X,
  Tag, Basket, TreeStructure, Bell, Gear, Circle, UserCircle,
  WhatsappLogo, Database, Shield, Buildings, IdentificationCard,
} from '@phosphor-icons/react';
import { useState, useRef, useEffect } from 'react';
import api from '../api';
import AriaButton from './aria/AriaButton';
import AriaPanel from './aria/AriaPanel';
import AriaCommandBar from './aria/AriaCommandBar';

const SEVERITY_COLOR = {
  critical: 'hsl(0,80%,55%)',
  success: 'hsl(142,70%,45%)',
  info: 'hsl(197,100%,48%)',
};

const NAV = [
  { to: '/', icon: Gauge, label: 'Control Tower', end: true },
  { to: '/customers', icon: Users, label: 'Customers' },
  { to: '/products', icon: Cube, label: 'Products & BOM' },
  { to: '/factory', icon: Factory, label: 'Factory' },
  { to: '/quality', icon: ClipboardText, label: 'Quality' },
  { to: '/inventory', icon: Package, label: 'Inventory' },
  { to: '/dispatch', icon: Truck, label: 'Dispatch' },
  { to: '/dispatch-plan', icon: CalendarBlank, label: 'Dispatch Plan' },
  { to: '/orders', icon: Tag, label: 'Sales Orders' },
  { to: '/finance', icon: CurrencyInr, label: 'Finance' },
  {
    label: 'Procurement',
    icon: Basket,
    basePath: '/procurement',
    children: [
      { to: '/procurement/vendors', label: 'Vendors' },
      { to: '/procurement/carriers', label: 'Carriers' },
      { to: '/procurement/services', label: 'Services' },
      { to: '/procurement/purchasing', label: 'Purchasing' },
    ],
  },
  { to: '/genealogy', icon: TreeStructure, label: 'Batch Genealogy' },
  { to: '/whatsapp', icon: WhatsappLogo, label: 'WhatsApp' },
];

function NavAccordion({ item, closeSidebar }) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(item.basePath);
  const Icon = item.icon;
  return (
    <>
      <NavLink
        to={item.children[0].to}
        onClick={closeSidebar}
        data-testid={`nav-${item.label.toLowerCase()}`}
        className={() => `sidebar-link${isActive ? ' active' : ''}`}
        style={{ justifyContent: 'space-between' }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon size={16} weight="regular" />
          <span>{item.label}</span>
        </span>
        <span style={{ fontSize: 8, color: 'hsl(0,0%,40%)', letterSpacing: 0 }}>
          {isActive ? '▲' : '▼'}
        </span>
      </NavLink>
      {isActive && item.children.map(child => (
        <NavLink
          key={child.to}
          to={child.to}
          end
          onClick={closeSidebar}
          data-testid={`nav-${child.label.toLowerCase()}`}
          className={({ isActive: ca }) => `sidebar-link${ca ? ' active' : ''}`}
          style={{ paddingLeft: 36, fontSize: 11 }}
        >
          <span style={{ color: 'hsl(0,0%,30%)', marginRight: 4 }}>—</span>
          <span>{child.label}</span>
        </NavLink>
      ))}
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { notifications, unreadCount, markAllRead, connected } = useRealtime();
  const { orgProfile } = useLocationContext();
  const navigate = useNavigate();
  const [alertCount, setAlertCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);

  useEffect(() => {
    api.get('/alerts').then(r => {
      setAlertCount(r.data.filter(a => !a.is_read).length);
    }).catch(() => {});
  }, []);

  // Close notif panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const closeSidebar = () => setSidebarOpen(false);

  const avatarUrl = user?.avatar_url;
  const initials = (user?.name || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  const handleNotifToggle = () => {
    if (!notifOpen) markAllRead();
    setNotifOpen(v => !v);
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
        <div style={{ padding: '20px 16px 16px', borderBottom: '1px solid hsl(0,0%,14%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {orgProfile?.logo_url && (
            <img src={orgProfile.logo_url} alt="logo" style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 16, fontWeight: 700,
              color: orgProfile?.branding?.primary_color || 'hsl(197,100%,48%)', letterSpacing: '0.1em', textTransform: 'uppercase',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {orgProfile?.trade_name || 'ARIA FORGE'}
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'hsl(0,0%,40%)',
              textTransform: 'uppercase', letterSpacing: '0.2em', marginTop: 2 }}>
              ERP · Manufacturing
            </div>
          </div>
        </div>
        <button onClick={closeSidebar} className="hamburger-btn"
          style={{ display: 'none' }} aria-label="Close sidebar">
          <X size={16} />
        </button>
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        <div style={{ padding: '8px 16px 4px', fontSize: 9, color: 'hsl(0,0%,35%)',
          textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700 }}>
          Workspaces
        </div>
        {NAV.map((item) => {
          if (item.children) {
            return <NavAccordion key={item.label} item={item} closeSidebar={closeSidebar} />;
          }
          const { to, icon: Icon, label, end } = item;
          return (
            <NavLink
              key={to} to={to} end={end}
              onClick={closeSidebar}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
            >
              <Icon size={16} weight="regular" />
              <span>{label}</span>
            </NavLink>
          );
        })}
        <div style={{ padding: '8px 16px 4px', marginTop: 4, fontSize: 9, color: 'hsl(0,0%,35%)',
          textTransform: 'uppercase', letterSpacing: '0.15em', fontWeight: 700 }}>
          Account
        </div>
        <NavLink to="/settings" onClick={closeSidebar}
          data-testid="nav-settings"
          className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
          <Gear size={16} weight="regular" />
          <span>Settings</span>
        </NavLink>
        {user?.role === 'admin' && (
          <NavLink to="/users" onClick={closeSidebar}
            data-testid="nav-users"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
            <UserCircle size={16} weight="regular" />
            <span>User Management</span>
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/masters" onClick={closeSidebar}
            data-testid="nav-masters"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
            <Database size={16} weight="regular" />
            <span>Master Tables</span>
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/audit-log" onClick={closeSidebar}
            data-testid="nav-audit-log"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
            <Shield size={16} weight="regular" />
            <span>Audit Log</span>
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/company-profile" onClick={closeSidebar}
            data-testid="nav-company-profile"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
            <IdentificationCard size={16} weight="regular" />
            <span>Company Profile</span>
          </NavLink>
        )}
        {user?.role === 'admin' && (
          <NavLink to="/locations" onClick={closeSidebar}
            data-testid="nav-locations"
            className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}>
            <Buildings size={16} weight="regular" />
            <span>Locations</span>
          </NavLink>
        )}
      </nav>

      {/* User + Logout */}
      <div style={{ borderTop: '1px solid hsl(0,0%,14%)', padding: 12 }}>
        <NavLink to="/settings"
          onClick={closeSidebar}
          style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8,
            marginBottom: 8, padding: '6px 8px', borderBottom: '1px solid hsl(0,0%,10%)',
            cursor: 'pointer' }}>
          {/* Avatar */}
          <div style={{ width: 28, height: 28, background: 'hsl(197,100%,48%)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
            color: '#fff', flexShrink: 0, overflow: 'hidden' }}>
            {avatarUrl
              ? <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials
            }
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'hsl(0,0%,90%)', fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 500, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name?.split(' ')[0]}
            </div>
            <div style={{ fontSize: 9, color: 'hsl(0,0%,45%)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {user?.role?.replace('_', ' ')}
            </div>
          </div>
        </NavLink>
        <button onClick={handleLogout} data-testid="logout-btn"
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 8px',
            background: 'transparent', border: 'none', cursor: 'pointer', color: 'hsl(0,0%,50%)',
            fontSize: 11, fontFamily: "'JetBrains Mono', monospace", textTransform: 'uppercase',
            letterSpacing: '0.08em', transition: 'color 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'hsl(0,84%,60%)'}
          onMouseLeave={e => e.currentTarget.style.color = 'hsl(0,0%,50%)'}>
          <SignOut size={14} />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'hsl(0,0%,3%)', overflow: 'hidden' }}>
      {/* Mobile backdrop */}
      <div className={`sidebar-backdrop${sidebarOpen ? ' open' : ''}`} onClick={closeSidebar} />

      {/* Sidebar */}
      <aside className={`sidebar-mobile${sidebarOpen ? ' open' : ''}`}
        style={{
          width: 220, minWidth: 220, height: '100vh', borderRight: '1px solid hsl(0,0%,14%)',
          display: 'flex', flexDirection: 'column', background: 'hsl(0,0%,3%)',
        }}>
        <SidebarContent />
      </aside>

      {/* Main */}
      <main className="main-area" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* Top bar */}
        <div style={{ height: 48, borderBottom: '1px solid hsl(0,0%,14%)', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', gap: 12,
          position: 'sticky', top: 0, background: 'hsl(0,0%,3%)', zIndex: 10 }}>

          <button className="hamburger-btn" data-testid="hamburger-btn"
            onClick={() => setSidebarOpen(true)} aria-label="Open sidebar">
            <List size={18} />
          </button>

          <div style={{ fontFamily: "'Clash Display', sans-serif", fontSize: 14, fontWeight: 700,
            color: 'hsl(197,100%,48%)', letterSpacing: '0.12em', textTransform: 'uppercase',
            display: 'none' }} className="mobile-logo">
            ARIA FORGE
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto', minWidth: 0, overflow: 'hidden' }}>
            {alertCount > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px',
                background: 'hsl(43,96%,56%)', color: '#000', fontSize: 11,
                fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, letterSpacing: '0.05em',
                flexShrink: 0 }}>
                <Warning size={12} weight="bold" />
                <span className="alerts-label">{alertCount} ALERTS</span>
              </div>
            )}

            <LocationSwitcher />
            <div style={{ position: 'relative' }} ref={notifRef}>
              <button
                data-testid="notif-bell-btn"
                onClick={handleNotifToggle}
                style={{
                  position: 'relative', background: 'transparent', border: 'none',
                  cursor: 'pointer', color: unreadCount > 0 ? 'hsl(197,100%,48%)' : 'hsl(0,0%,45%)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: 6, transition: 'color 0.15s',
                }}>
                <Bell size={18} weight={unreadCount > 0 ? 'fill' : 'regular'} />
                {unreadCount > 0 && (
                  <span data-testid="notif-badge" style={{
                    position: 'absolute', top: 0, right: 0,
                    background: 'hsl(0,80%,55%)', color: '#fff',
                    fontSize: 8, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace",
                    minWidth: 14, height: 14, borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, padding: '0 2px',
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown */}
              {notifOpen && (
                <div data-testid="notif-panel" style={{
                  position: 'absolute', right: 0, top: 40,
                  width: 320, maxHeight: 400, overflowY: 'auto',
                  background: 'hsl(0,0%,7%)', border: '1px solid hsl(0,0%,18%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.6)', zIndex: 100,
                }}>
                  <div style={{ padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,14%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                      fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em',
                      color: 'hsl(0,0%,80%)' }}>
                      Live Events
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Circle size={6} weight="fill"
                        color={connected ? 'hsl(142,70%,45%)' : 'hsl(0,0%,40%)'}
                      />
                      <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono', monospace",
                        color: connected ? 'hsl(142,70%,45%)' : 'hsl(0,0%,40%)',
                        textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                        {connected ? 'Live' : 'Offline'}
                      </span>
                    </div>
                  </div>

                  {notifications.length === 0 ? (
                    <div style={{ padding: 20, textAlign: 'center',
                      fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                      color: 'hsl(0,0%,35%)' }}>
                      No events yet
                    </div>
                  ) : (
                    notifications.map(n => (
                      <div key={n.id} data-testid="notif-item" style={{
                        padding: '10px 14px', borderBottom: '1px solid hsl(0,0%,10%)',
                        borderLeft: `3px solid ${SEVERITY_COLOR[n.severity] || 'hsl(0,0%,30%)'}`,
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em',
                            color: SEVERITY_COLOR[n.severity] || 'hsl(0,0%,55%)' }}>
                            {n.label}
                          </span>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                            color: 'hsl(0,0%,35%)' }}>
                            {new Date(n.ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10,
                          color: 'hsl(0,0%,60%)', lineHeight: 1.4 }}>
                          {n.message}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <div style={{ fontSize: 10, color: 'hsl(0,0%,35%)', fontFamily: "'JetBrains Mono', monospace",
              textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap' }}>
              {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Page content */}
        <div className="page-content" style={{ flex: 1, padding: '24px' }}>
          <Outlet />
        </div>
      </main>

      <AriaButton />
      <AriaPanel />
      <AriaCommandBar />

      <style>{`
        @media (max-width: 768px) {
          .mobile-logo { display: block !important; }
        }
      `}</style>
    </div>
  );
}
